import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Popper, Paper, ClickAwayListener, Divider } from "@material-ui/core";
import { ArrowDropDown as ArrowDropDownIcon } from "@material-ui/icons";

import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";

const Frame = styled.div`
    padding: 0px 10px;
    display: flex;
    align-items: center;

    > div:first-child {
        padding-left: 10px;
    }

    &:before {
        content: "";
        width: 1px;
        height: 50%;
        background: #DBDBDB;
    }
`;

const FilterTrigger = styled.div`
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    position: relative;
    padding-left: 10px;

    .dropdown-arrow {
        color: rgba(0, 0, 0, 0.54);
        font-size: 20px;
        margin-left: 2px;
        transition: transform 0.2s ease;
        ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
    }
`;

const TriggerText = styled.span`
    color: #444;
    font-family: "Source Sans Pro";
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 15px;
    text-transform: uppercase;
`;

const MenuPaper = styled(Paper)`
    &&& {
        padding: 5px 0;
        box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.20);
        background: #ffffff;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px 15px;
`;

const HeaderLabel = styled.span`
    color: #888;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
`;

const TopOptionItem = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${({ isSelected }) => (isSelected ? themeColors.ui_blue : themeColors.darkestGray)};
    cursor: pointer;
    user-select: none;
    padding: 12px 0;

    &:hover {
        color: ${themeColors.ui_blue};
    }
`;

const SearchInputContainer = styled.div`
    padding: 10px 15px;
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: 10px;
    border: 1px solid #DDD;
    border-radius: 1px;
    font-size: 14px;
    font-family: inherit;
    color: #333;
    outline: none;

    &::placeholder {
        font-style: italic;
        color: #999;
    }

    &:focus {
        border-color: ${themeColors.ui_blue};
    }
`;

const OptionsListContainer = styled.div`
    overflow-y: ${({ searchable }) => searchable ? "auto" : "hidden"};
    padding: 0;
    max-height: ${({ searchable }) => searchable ? "220px" : "none"};

    &::-webkit-scrollbar {
        width: 5px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #bbb;
        border-radius: 4px;
    }
`;

const OptionItemRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 15px;
    cursor: pointer;
    user-select: none;
    background-color: ${({ isHighlighted }) => (isHighlighted ? "#f0f4f8" : "transparent")};

    &:hover {
        background-color: #f7fafc;
    }
`;

const OptionName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${({ isSelected }) => (isSelected ? themeColors.ui_blue : themeColors.darkestGray)};
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 150%; 
`;

const OptionCount = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${themeColors.gray};
    flex-shrink: 0;
`;

const EmptyMessage = styled.div`
    padding: 10px 15px;
    color: ${themeColors.gray};
    font-size: 13px;
    text-align: center;
    font-style: italic;
`;

export function FilterDropDown({
    filterOptions = [],
    onChange,
    selectedFilter,
    title,
    searchable = false,
    searchPlaceholder = "",
    className = ""
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const inputRef = useRef(null);

    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const itemRefs = useRef([]);

    const isOpen = Boolean(anchorEl);

    const handleToggle = event => {
        if (isOpen) {
            setAnchorEl(null);
            setSearchQuery("");
            setHighlightedIndex(-1);
        } else {
            setAnchorEl(event.currentTarget);
            setSearchQuery("");
            setHighlightedIndex(-1);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchQuery("");
        setHighlightedIndex(-1);
    };

    const handleClickAway = event => {
        if (anchorEl && anchorEl.contains(event.target)) {
            return;
        }
        handleClose();
    };

    useEffect(() => {
        if (isOpen && searchable) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [isOpen, searchable]);

    const options = (filterOptions || []).map(opt => ({
        field: opt.field,
        name: opt.name,
        count: opt.count,
        isPinned: Boolean(opt.isPinned || opt.field === "__all__")
    }));

    const topOptions = options.filter(o => o.isPinned);
    const listOptions = options.filter(o => !o.isPinned);

    const activeOption = options.find(o => o.field === selectedFilter) || topOptions[0];
    const triggerLabel = activeOption ? activeOption.name : (selectedFilter || "");

    const filteredOptions = searchQuery.trim()
        ? listOptions.filter(o => o.name.toLowerCase().includes(searchQuery.trim().toLowerCase()))
        : listOptions;

    const handleSelect = field => {
        onChange({ target: { value: field } });
        handleClose();
    };

    const handleKeyDown = e => {
        if (e.key === "Escape") {
            handleClose();
            return;
        }

        if (["ArrowDown", "ArrowUp", "Enter"].includes(e.key)) {
            e.preventDefault();
        }

        const moveHighlight = step => {
            if (!filteredOptions.length) {
                return;
            }
            const nextIndex = step > 0
                ? (highlightedIndex < filteredOptions.length - 1 ? highlightedIndex + 1 : 0)
                : (highlightedIndex > 0 ? highlightedIndex - 1 : filteredOptions.length - 1);
            setHighlightedIndex(nextIndex);
            itemRefs.current[nextIndex]?.scrollIntoView?.({ block: "nearest" });
        };

        if (e.key === "ArrowDown") {
            moveHighlight(1);
        } else if (e.key === "ArrowUp") {
            moveHighlight(-1);
        } else if (e.key === "Enter") {
            const selected = highlightedIndex >= 0 ? filteredOptions[highlightedIndex] : filteredOptions[0];
            if (selected) {
                handleSelect(selected.field);
            }
        }
    };

    return (
        <Frame className={className}>
            <FilterTrigger
                isOpen={isOpen}
                onClick={handleToggle}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <TriggerText>{triggerLabel}</TriggerText>
                <ArrowDropDownIcon className="dropdown-arrow" />
            </FilterTrigger>
            <Popper
                open={isOpen}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
                modifiers={{
                    flip: { enabled: true },
                    preventOverflow: { enabled: true, boundariesElement: "viewport" }
                }}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <MenuPaper>
                        {(title || topOptions.length > 0) && (
                            <HeaderContainer>
                                {title && <HeaderLabel>{title}</HeaderLabel>}
                                {topOptions.map((opt, index) => {
                                    const isSelected = (!selectedFilter && index === 0) || selectedFilter === opt.field;
                                    return (
                                        <TopOptionItem
                                            key={opt.field}
                                            isSelected={isSelected}
                                            onClick={() => handleSelect(opt.field)}
                                        >
                                            {opt.name}
                                        </TopOptionItem>
                                    );
                                })}
                            </HeaderContainer>
                        )}
                        {searchable && (
                            <>
                                <Divider margin={5} color="#eee" />
                                <SearchInputContainer>
                                    <SearchInput
                                        ref={inputRef}
                                        type="text"
                                        placeholder={searchPlaceholder || t("Search Tags...")}
                                        value={searchQuery}
                                        onChange={e => {
                                            setSearchQuery(e.target.value);
                                            setHighlightedIndex(-1);
                                        }}
                                        onKeyDown={handleKeyDown}
                                    />
                                </SearchInputContainer>
                            </>
                        )}
                        <OptionsListContainer searchable={searchable}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((option, index) => {
                                    const isSelected = selectedFilter === option.field;
                                    const isHighlighted = highlightedIndex === index;
                                    return (
                                        <OptionItemRow
                                            key={option.field}
                                            ref={el => {
                                                itemRefs.current[index] = el;
                                            }}
                                            isSelected={isSelected}
                                            isHighlighted={isHighlighted}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            onClick={() => handleSelect(option.field)}
                                        >
                                            <OptionName isSelected={isSelected} title={option.name}>
                                                {option.name}
                                            </OptionName>
                                            {option.count !== undefined && (
                                                <OptionCount>
                                                    ({option.count})
                                                </OptionCount>
                                            )}
                                        </OptionItemRow>
                                    );
                                })
                            ) : (
                                <EmptyMessage>
                                    {listOptions.length === 0 ? t("No options available") : t("No matching options")}
                                </EmptyMessage>
                            )}
                        </OptionsListContainer>
                    </MenuPaper>
                </ClickAwayListener>
            </Popper>
        </Frame>
    );
}
